import { useEffect, useState } from "react";

import { getHome, type Home as HomeData } from "../lib/api";
import { human, iso, relative } from "../lib/calendar";
import WeekStrip from "../components/WeekStrip";
import Onboarding from "../components/Onboarding";
import { dismissed, shouldOnboard } from "../lib/onboarding";
import { plural, wordFor } from "../lib/format";
import { haptic } from "../lib/telegram";
import PixelField from "../components/PixelField";
import SessionCard from "../components/SessionCard";
import { plannedKinds } from "../lib/setRows";
import { useSession } from "../store/session";
import InsightList from "../components/InsightList";
import { BusyLabel } from "../components/DotLoader";

interface Props {
  onStartDay: (dayId: number) => void;
  onContinue: () => void;
  onOpenSession: (clientId: string) => void;
  onOpenExercise: (exerciseId: number) => void;
  onOpenPrograms: () => void;
  onOpenCalendar: () => void;
  onOpenTemplates: () => void;
}

const fmt = (v: number) => Math.round(v).toLocaleString("ru-RU");

/**
 * Рекорд округлять до целого нельзя: 82.5 превращалось в 83 — вес, которого
 * не поднимали. Дробь через точку, как на экране тренировки и в истории.
 */
const kg = (v: number) => String(Number(v.toFixed(1)));


/** «Завтра», «в пятницу» или «3 октября» — так о ближайших днях говорят вслух. */
const whenLabel = (day: string): string => {
  const today = iso(new Date());
  return relative(day, today) ?? human(day, today);
};

const RECORD_LABEL: Record<string, string> = {
  max_weight: "рабочий вес",
  est_1rm: "оценка 1ПМ",
  max_volume_session: "объём за тренировку",
  max_reps_at_weight: "повторы на весе",
};

/**
 * Главная.
 *
 * Приложение открывают в двух состояниях: перед тренировкой — начать, и в день
 * без тренировки — посмотреть, что изменилось. Верхний блок обслуживает первое
 * и всегда несёт ровно одно действие; всё остальное отвечает на второе.
 *
 * Ленты и чужих тренировок здесь нет и не будет: §11 ТЗ отказался от
 * соцфункций, и протаскивать их через главную — обход собственного решения.
 */
export default function Home({
  onStartDay,
  onContinue,
  onOpenSession,
  onOpenExercise,
  onOpenPrograms,
  onOpenCalendar,
  onOpenTemplates,
}: Props) {
  const starting = useSession((s) => s.starting);
  const startError = useSession((s) => s.startError);
  const { active } = useSession();
  const [data, setData] = useState<HomeData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [skipped, setSkipped] = useState(dismissed);

  const load = () =>
    getHome()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));

  useEffect(() => {
    void load();
  }, []);

  // Новый человек: ни программы, ни тренировки — сначала знакомство
  // (21-retention-plan.md §6), а не пустая главная.
  if (data && !active && shouldOnboard(Boolean(data.next_workout), Boolean(data.last_session), skipped)) {
    return (
      <div className="screen">
        <Onboarding
          onStarted={() => void load()}
          onBrowse={onOpenTemplates}
          onSkip={() => setSkipped(true)}
        />
      </div>
    );
  }

  // Прогресс идущей тренировки — тем же счётом, что на её экране: без разминки.
  const planned = active ? active.plan.reduce((n, p) => n + plannedKinds(p).length, 0) : 0;
  const done = active ? active.sets.filter((s) => s.kind !== "warmup").length : 0;

  return (
    <div className="screen">
      <div className="header">
        <span className="eyebrow">RACK</span>
        {data && data.streak_weeks > 1 && (
          <span className="eyebrow">
            {plural(data.streak_weeks, "неделя", "недели", "недель")} подряд
          </span>
        )}
      </div>

      {/* Верхний блок — одно действие. Идущая тренировка вытесняет всё
          остальное: пока она не закончена, других решений у человека нет. */}
      {active ? (
        <div className="hero hero--primary">
          <PixelField className="hero__field" seed={1} />
          <span className="hero__eyebrow hero__eyebrow--live">Тренировка идёт</span>
          <span className="hero__title">{active.title}</span>
          <span className="hero__meta">
            {planned > 0
              ? `${done} из ${plural(planned, "подхода", "подходов", "подходов")}`
              : `${plural(active.sets.length, "подход", "подхода", "подходов")} записано`}
          </span>
          {planned > 0 && (
            <span className="hero__progress" aria-hidden="true">
              <span style={{ width: `${Math.min(100, (done / planned) * 100)}%` }} />
            </span>
          )}
          <button className="button button--wide" onClick={() => {
            haptic("medium");
            onContinue();
          }}>
            Продолжить
          </button>
        </div>
      ) : data?.next_workout ? (
        <div className="hero hero--primary">
          <PixelField className="hero__field" seed={1} />
          {/* Что говорит календарь. Без плана остаётся прежняя подпись — она
              честно называет вещи своими именами: это порядок, не расписание. */}
          <span className="hero__eyebrow">
            {data.next_workout.rest_today
              ? "Сегодня отдых"
              : data.next_workout.planned_date
                ? "Сегодня по плану"
                : "Следующая по порядку"}
          </span>
          <span className="hero__title">{data.next_workout.day_name}</span>
          <span className="hero__meta">
            {data.next_workout.rest_today && data.next_workout.planned_date
              ? `${whenLabel(data.next_workout.planned_date)} · `
              : ""}
            {data.next_workout.program_name} · {data.next_workout.block_name} ·{" "}
            {plural(data.next_workout.exercise_count, "упражнение", "упражнения", "упражнений")}
          </span>
          <button
            className="button button--wide"
            disabled={data.next_workout.exercise_count === 0 || starting !== null}
            onClick={() => {
              haptic("medium");
              onStartDay(data.next_workout!.program_day_id);
            }}
          >
            {starting === data.next_workout.program_day_id ? (
              <BusyLabel text="Начинаем" />
            ) : data.next_workout.rest_today ? (
              "Начать раньше"
            ) : (
              "Начать"
            )}
          </button>
          {startError?.day === data.next_workout.program_day_id && (
            <span className="hero__meta" role="alert">
              {startError.message}
            </span>
          )}
        </div>
      ) : (
        data && (
          // Главная не говорит «пусто» — она предлагает начать. Раньше новый
          // человек видел здесь «Программы пока нет» и уходил (20-review.md §4.2).
          <div className="hero hero--primary">
            <PixelField className="hero__field" seed={7} />
            <span className="hero__eyebrow">С чего начать</span>
            <span className="hero__title">Выбери программу</span>
            <span className="hero__meta">
              Готовые — для зала, для дома, для новичка. Одна кнопка — и первая
              тренировка уже сегодня.
            </span>
            <button className="button button--wide" onClick={onOpenTemplates}>
              Выбрать программу
            </button>
            <button className="hero__link" onClick={onOpenPrograms}>
              Есть своя таблица — к импорту
            </button>
          </div>
        )
      )}

      {error && <p className="lead lead--error">{error}</p>}

      {/* Календарь больше не прячется ссылкой в углу: неделя стоит под главным
          блоком и ведёт внутрь (19-home-and-bot-cards.md §2). */}
      {data && (
        <WeekStrip
          today={iso(new Date())}
          planned={data.week}
          absences={data.absence ? [data.absence] : []}
          onOpen={onOpenCalendar}
        />
      )}

      {data && (
        <div className="tiles">
          <div className="tile">
            <span className="tile__value">{data.week_workouts}</span>
            <span className="tile__key">
              {wordFor(data.week_workouts, "тренировка", "тренировки", "тренировок")} на неделе
            </span>
          </div>
          <div className="tile">
            <span className="tile__value">{fmt(data.week_volume_kg)}</span>
            <span className="tile__key">кг за неделю</span>
          </div>
        </div>
      )}

      {data?.recent_records && data.recent_records.length > 0 && (
        <div className="card">
          <p className="card__title">Свежие рекорды</p>
          {data.recent_records.map((r) => (
            <div key={`${r.exercise_id}-${r.kind}`} className="row">
              {/* Вид рекорда — вторым уровнем под названием: в одну строку с
                  весом он переносился и рвал её пополам. */}
              <span className="row__key lift__body">
                <span className="lift__name">{r.exercise_name}</span>
                <span className="lift__hint">{RECORD_LABEL[r.kind] ?? r.kind}</span>
              </span>
              <span className="row__val pr__value">{kg(r.value)} кг</span>
            </div>
          ))}
        </div>
      )}

      {data?.last_session && (
        <SessionCard
          session={data.last_session}
          eyebrow="Последняя тренировка"
          onOpen={onOpenSession}
        />
      )}

      {data && (
        <InsightList items={data.insights} onOpen={onOpenExercise} />
      )}
    </div>
  );
}
